import { SlideDrawer } from '@/components/ui/slide-drawer';
import { X, AlertTriangle, AlertCircle, Info, Lightbulb, MapPin } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ValidationIssue } from '@/types/workflow-graph';

// =============================================================================
// Types
// =============================================================================

interface ValidationIssuesDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    errors: ValidationIssue[];
    warnings: ValidationIssue[];
    /** Callback when user clicks on an issue to focus the node */
    onFocusNode?: (nodeId: string) => void;
}

// Human-readable labels for issue types
const ISSUE_TYPE_LABELS: Record<string, string> = {
    invalid_merge: 'Invalid Merge',
    missing_join: 'Missing Join',
    orphan_node: 'Orphan Node',
    unreachable_state: 'Unreachable State',
    invalid_connection: 'Invalid Connection',
    duplicate_connection: 'Duplicate Connection',
    terminal_with_output: 'Terminal State Issue',
    missing_default_branch: 'Missing Default Branch',
    cycle_detected: 'Cycle Detected',
    invalid_start_at: 'Invalid Start State',
    missing_next_or_end: 'Missing Transition',
    invalid_state_type: 'Invalid State Type',
};

// =============================================================================
// Sub-components
// =============================================================================

function IssueCard({
    issue,
    onFocusNode,
}: {
    issue: ValidationIssue;
    onFocusNode?: (nodeId: string) => void;
}) {
    const isError = issue.severity === 'error';
    const label = ISSUE_TYPE_LABELS[issue.type] || issue.type;

    return (
        <div
            className={cn(
                'rounded-lg border p-4 transition-colors',
                isError
                    ? 'border-red-200 bg-red-50/50 hover:bg-red-50'
                    : 'border-amber-200 bg-amber-50/50 hover:bg-amber-50'
            )}
        >
            {/* Header */}
            <div className="flex items-start gap-3">
                <div
                    className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                        isError ? 'bg-red-100' : 'bg-amber-100'
                    )}
                >
                    {isError ? (
                        <AlertCircle size={16} className="text-red-600" />
                    ) : (
                        <AlertTriangle size={16} className="text-amber-600" />
                    )}
                </div>

                <div className="flex-1 min-w-0">
                    {/* Issue Type Badge + Severity */}
                    <div className="flex items-center gap-2 mb-1">
                        <Badge
                            variant="outline"
                            className={cn(
                                'text-[10px] px-1.5 py-0 font-medium',
                                isError
                                    ? 'border-red-300 text-red-700 bg-red-100'
                                    : 'border-amber-300 text-amber-700 bg-amber-100'
                            )}
                        >
                            {isError ? 'ERROR' : 'WARNING'}
                        </Badge>
                        <span className="text-xs font-medium text-slate-500">{label}</span>
                    </div>

                    {/* Message */}
                    <p className="text-sm text-slate-800 leading-relaxed">{issue.message}</p>

                    {/* Node IDs */}
                    {issue.nodeIds && issue.nodeIds.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {issue.nodeIds.map((nodeId) => (
                                <button
                                    key={nodeId}
                                    onClick={() => onFocusNode?.(nodeId)}
                                    className={cn(
                                        'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-mono transition-colors',
                                        'border-slate-200 bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                                        onFocusNode && 'cursor-pointer'
                                    )}
                                    disabled={!onFocusNode}
                                >
                                    <MapPin size={12} />
                                    {nodeId.length > 20 ? `${nodeId.substring(0, 20)}...` : nodeId}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Auto-fix suggestion */}
                    {issue.autoFix && (
                        <div className="mt-3 flex items-center gap-2 rounded-md bg-blue-50 border border-blue-200 px-3 py-2">
                            <Lightbulb size={14} className="text-blue-600 shrink-0" />
                            <span className="text-xs text-blue-800">{issue.autoFix.description}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function EmptyState() {
    return (
        <div className="flex flex-col items-center justify-center p-12 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mb-4">
                <svg
                    className="h-8 w-8 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
            </div>
            <p className="text-lg font-medium text-slate-700">No issues found!</p>
            <p className="text-sm text-slate-500 mt-1">Your workflow is ready for export.</p>
        </div>
    );
}

// =============================================================================
// Main Component
// =============================================================================

export default function ValidationIssuesDrawer({
    isOpen,
    onClose,
    errors,
    warnings,
    onFocusNode,
}: ValidationIssuesDrawerProps) {
    const totalIssues = errors.length + warnings.length;
    const hasErrors = errors.length > 0;
    const hasWarnings = warnings.length > 0;

    return (
        <SlideDrawer isOpen={isOpen} onClose={onClose}>
                        {/* Header */}
                        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                                        <AlertCircle className="h-5 w-5 text-red-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-semibold text-slate-900">
                                            Validation Issues
                                        </h2>
                                        <p className="text-sm text-slate-500">
                                            {totalIssues === 0
                                                ? 'No issues found'
                                                : `${totalIssues} issue${totalIssues !== 1 ? 's' : ''} found`}
                                            {hasErrors && (
                                                <span className="text-red-600 font-medium">
                                                    {' '}
                                                    · {errors.length} error{errors.length !== 1 ? 's' : ''}
                                                </span>
                                            )}
                                            {hasWarnings && (
                                                <span className="text-amber-600 font-medium">
                                                    {' '}
                                                    · {warnings.length} warning{warnings.length !== 1 ? 's' : ''}
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onClose();
                                    }}
                                    className="rounded-full p-2 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors outline-none focus:ring-2 focus:ring-slate-900"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto bg-white py-4 px-6">
                            {totalIssues === 0 ? (
                                <EmptyState />
                            ) : (
                                <div className="space-y-6">
                                    {/* Info Banner */}
                                    {hasErrors && (
                                        <div className="flex items-start gap-3 rounded-lg bg-red-50 border border-red-200 p-4">
                                            <Info size={18} className="text-red-600 shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-sm font-medium text-red-800">
                                                    Export blocked
                                                </p>
                                                <p className="text-sm text-red-700 mt-0.5">
                                                    Resolve all errors before exporting the workflow.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Errors Section */}
                                    {hasErrors && (
                                        <div>
                                            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                                <AlertCircle size={14} className="text-red-500" />
                                                Errors ({errors.length})
                                            </h3>
                                            <div className="space-y-3">
                                                {errors.map((issue, index) => (
                                                    <IssueCard
                                                        key={`error-${index}`}
                                                        issue={issue}
                                                        onFocusNode={onFocusNode}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Warnings Section */}
                                    {hasWarnings && (
                                        <div>
                                            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                                <AlertTriangle size={14} className="text-amber-500" />
                                                Warnings ({warnings.length})
                                            </h3>
                                            <div className="space-y-3">
                                                {warnings.map((issue, index) => (
                                                    <IssueCard
                                                        key={`warning-${index}`}
                                                        issue={issue}
                                                        onFocusNode={onFocusNode}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end items-center gap-3">
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onClose();
                                }}
                                className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-md transition-all hover:bg-slate-50 hover:shadow-lg"
                            >
                                Close
                            </button>
                        </div>
        </SlideDrawer>
    );
}

